/**
 * Badges — Server-backed achievement system.
 *
 * Badge categories:
 *   - achievement: workout milestones, PR milestones
 *   - season: season champion, season participation
 *   - challenge: weekly challenge wins, streaks
 *   - social: duels, community engagement
 */

import { supabase } from './supabase';
import { createLogger } from './logger';

const log = createLogger('BADGES');

// ────────────────────────────────────────────
// Badge Definitions
// ────────────────────────────────────────────

export interface BadgeDef {
  key: string;
  label: string;
  emoji: string;
  description: string;
  category: 'achievement' | 'season' | 'challenge' | 'social';
}

export const BADGE_DEFS: BadgeDef[] = [
  // Achievement
  { key: 'first_workout',    label: 'Iron Initiate',       emoji: '🏋️', description: 'Logged your first workout',                 category: 'achievement' },
  { key: 'workout_10',       label: 'Consistency',         emoji: '💪', description: '10 workouts logged',                         category: 'achievement' },
  { key: 'workout_50',       label: 'Dedicated',           emoji: '🔥', description: '50 workouts logged',                         category: 'achievement' },
  { key: 'workout_100',      label: 'Century Club',        emoji: '💯', description: '100 workouts logged',                        category: 'achievement' },
  { key: 'workout_365',      label: 'Year of Iron',        emoji: '📅', description: '365 workouts logged',                        category: 'achievement' },
  { key: 'baseline_complete', label: 'Ranked',             emoji: '✅', description: 'All 5 Core 5 lifts logged',                category: 'achievement' },
  { key: 'first_pr',         label: 'Record Breaker',      emoji: '🚀', description: 'Hit your first personal record',             category: 'achievement' },
  { key: 'pr_10',            label: 'PR Machine',          emoji: '⚡', description: '10 personal records set',                    category: 'achievement' },
  { key: 'video_verified',   label: 'Verified Lifter',     emoji: '📹', description: 'First lift with video proof',                category: 'achievement' },

  // Social / Duels
  { key: 'first_duel_win',   label: 'Victor',              emoji: '⚔️', description: 'Won your first duel',                        category: 'social' },
  { key: 'duel_wins_10',     label: 'Gladiator',           emoji: '🏆', description: '10 duel victories',                          category: 'social' },
  { key: 'duel_wins_50',     label: 'Champion',            emoji: '👑', description: '50 duel victories',                          category: 'social' },
  { key: 'first_challenge',  label: 'Challenger',          emoji: '🎯', description: 'Submitted first weekly challenge',           category: 'challenge' },
  { key: 'challenge_win',    label: 'Challenge Champion',  emoji: '🥇', description: 'Won a weekly challenge',                     category: 'challenge' },
  { key: 'challenge_streak_4', label: '4-Week Streak',     emoji: '🔗', description: 'Submitted challenges 4 weeks in a row',      category: 'challenge' },

  // Season
  { key: 'season_participant', label: 'Season Competitor', emoji: '🛡️', description: 'Registered for a season',                    category: 'season' },
  { key: 'season_top_10',     label: 'Season Top 10',     emoji: '🎖️', description: 'Finished in the top 10 of a season',          category: 'season' },
  { key: 'season_champion',   label: 'Season Champion',   emoji: '👑', description: 'Won a season championship',                   category: 'season' },

  // Strength milestones
  { key: 'bench_2plate',      label: '2-Plate Bench',      emoji: '🔩', description: 'Benched 225lbs / 100kg',                     category: 'achievement' },
  { key: 'bench_3plate',      label: '3-Plate Bench',      emoji: '🔩', description: 'Benched 315lbs / 140kg',                     category: 'achievement' },
  { key: 'squat_3plate',      label: '3-Plate Squat',      emoji: '🦵', description: 'Squatted 315lbs / 140kg',                    category: 'achievement' },
  { key: 'squat_4plate',      label: '4-Plate Squat',      emoji: '🦵', description: 'Squatted 405lbs / 185kg',                    category: 'achievement' },
  { key: 'deadlift_4plate',   label: '4-Plate Deadlift',   emoji: '💀', description: 'Deadlifted 405lbs / 185kg',                  category: 'achievement' },
  { key: 'deadlift_5plate',   label: '5-Plate Deadlift',   emoji: '💀', description: 'Deadlifted 495lbs / 225kg',                  category: 'achievement' },
  { key: '1000lb_club',       label: '1000lb Club',        emoji: '🏛️', description: 'Big 3 total ≥ 1000lbs',                      category: 'achievement' },
];

export const BADGE_MAP = new Map(BADGE_DEFS.map(b => [b.key, b]));

// ────────────────────────────────────────────
// DB Operations
// ────────────────────────────────────────────

export interface Badge {
  id: string;
  badge_key: string;
  badge_label: string;
  badge_emoji: string;
  badge_description: string | null;
  category: string;
  awarded_at: string;
  metadata: Record<string, any>;
}

/** Fetch all badges for a profile */
export async function fetchBadges(profileId: string): Promise<Badge[]> {
  const { data, error } = await supabase
    .from('earned_badges')
    .select('*')
    .eq('profile_id', profileId)
    .order('awarded_at', { ascending: false });

  if (error) {
    log.error('fetchBadges failed:', error);
    return [];
  }
  return data ?? [];
}

/** Award a badge (idempotent via upsert on profile_id + badge_key) */
export async function awardBadge(
  profileId: string,
  badgeKey: string,
  metadata: Record<string, any> = {},
): Promise<boolean> {
  const def = BADGE_MAP.get(badgeKey);
  if (!def) {
    log.warn('Unknown badge key:', badgeKey);
    return false;
  }

  const { error } = await supabase
    .from('earned_badges')
    .upsert({
      profile_id: profileId,
      badge_key: badgeKey,
      badge_label: def.label,
      badge_emoji: def.emoji,
      badge_description: def.description,
      category: def.category,
      metadata,
    }, { onConflict: 'profile_id,badge_key', ignoreDuplicates: true });

  if (error) {
    log.error('awardBadge failed:', error);
    return false;
  }
  return true;
}

/** Check if a profile has a specific badge */
export async function hasBadge(profileId: string, badgeKey: string): Promise<boolean> {
  const { count } = await supabase
    .from('earned_badges')
    .select('id', { count: 'exact', head: true })
    .eq('profile_id', profileId)
    .eq('badge_key', badgeKey);
  return (count ?? 0) > 0;
}

// ────────────────────────────────────────────
// Post-workout badge evaluation
// ────────────────────────────────────────────

/** Run after every workout save to check for new badges */
export async function evaluateBadges(profileId: string): Promise<string[]> {
  const awarded: string[] = [];

  try {
    // Get existing badges to skip checks
    const existing = await fetchBadges(profileId);
    const has = new Set(existing.map(b => b.badge_key));

    // ── Workout count badges ──
    const { count: workoutCount } = await supabase
      .from('workouts')
      .select('id', { count: 'exact', head: true })
      .eq('user_id', profileId);
    const wc = workoutCount ?? 0;

    const workoutThresholds: [number, string][] = [
      [1, 'first_workout'], [10, 'workout_10'], [50, 'workout_50'],
      [100, 'workout_100'], [365, 'workout_365'],
    ];
    for (const [threshold, key] of workoutThresholds) {
      if (wc >= threshold && !has.has(key)) {
        if (await awardBadge(profileId, key, { workoutCount: wc })) {
          awarded.push(key);
          // Fire first workout notification
          if (key === 'first_workout') {
            try {
              const { notifyFirstWorkout } = await import('@/lib/notificationTriggers');
              notifyFirstWorkout(profileId).catch(() => {});
            } catch {}
          }
        }
      }
    }

    // ── Duel win badges ──
    const { count: duelWins } = await supabase
      .from('duels')
      .select('id', { count: 'exact', head: true })
      .eq('winner_user_id', profileId)
      .in('status', ['completed', 'resolved']);
    const dw = duelWins ?? 0;

    const duelThresholds: [number, string][] = [
      [1, 'first_duel_win'], [10, 'duel_wins_10'], [50, 'duel_wins_50'],
    ];
    for (const [threshold, key] of duelThresholds) {
      if (dw >= threshold && !has.has(key)) {
        if (await awardBadge(profileId, key, { duelWins: dw })) awarded.push(key);
      }
    }

    // ── Baseline complete ──
    if (!has.has('baseline_complete')) {
      const { data: prof } = await supabase
        .from('profiles')
        .select('duel_standard_strength_kg')
        .eq('id', profileId)
        .maybeSingle();
      if (prof?.duel_standard_strength_kg && prof.duel_standard_strength_kg > 0) {
        if (await awardBadge(profileId, 'baseline_complete')) awarded.push('baseline_complete');
      }
    }

    // ── PR badges ──
    if (!has.has('first_pr') || !has.has('pr_10')) {
      const { count: prCount } = await supabase
        .from('posts')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', profileId)
        .eq('post_type', 'pr');
      const pc = prCount ?? 0;
      if (pc >= 1 && !has.has('first_pr')) {
        if (await awardBadge(profileId, 'first_pr', { prCount: pc })) awarded.push('first_pr');
      }
      if (pc >= 10 && !has.has('pr_10')) {
        if (await awardBadge(profileId, 'pr_10', { prCount: pc })) awarded.push('pr_10');
      }
    }

    // ── Video verified ──
    if (!has.has('video_verified')) {
      const { count: videoCount } = await supabase
        .from('strength_events')
        .select('id', { count: 'exact', head: true })
        .eq('profile_id', profileId)
        .not('video_url', 'is', null);
      if ((videoCount ?? 0) > 0) {
        if (await awardBadge(profileId, 'video_verified')) awarded.push('video_verified');
      }
    }

    // ── Strength milestone badges ──
    // Fetch best lifts
    const { data: bestLifts } = await supabase
      .from('strength_events')
      .select('lift_key, weight, weight_unit')
      .eq('profile_id', profileId)
      .in('lift_key', ['bench-press', 'back-squat', 'deadlift'])
      .order('weight', { ascending: false });

    if (bestLifts && bestLifts.length > 0) {
      const bestByLift: Record<string, number> = {};
      for (const row of bestLifts) {
        const lbs = row.weight_unit === 'kg' ? row.weight * 2.20462 : row.weight;
        if (!bestByLift[row.lift_key] || lbs > bestByLift[row.lift_key]) {
          bestByLift[row.lift_key] = lbs;
        }
      }

      const bench = bestByLift['bench-press'] ?? 0;
      const squat = bestByLift['back-squat'] ?? 0;
      const dead = bestByLift['deadlift'] ?? 0;

      const liftChecks: [number, string, string][] = [
        [225, 'bench-press', 'bench_2plate'],
        [315, 'bench-press', 'bench_3plate'],
        [315, 'back-squat', 'squat_3plate'],
        [405, 'back-squat', 'squat_4plate'],
        [405, 'deadlift', 'deadlift_4plate'],
        [495, 'deadlift', 'deadlift_5plate'],
      ];

      for (const [threshold, lift, key] of liftChecks) {
        const best = bestByLift[lift] ?? 0;
        if (best >= threshold && !has.has(key)) {
          if (await awardBadge(profileId, key, { weight: best, lift })) awarded.push(key);
        }
      }

      // 1000lb club
      if (bench + squat + dead >= 1000 && !has.has('1000lb_club')) {
        if (await awardBadge(profileId, '1000lb_club', { total: bench + squat + dead })) awarded.push('1000lb_club');
      }
    }

    // ── Challenge badges ──
    if (!has.has('first_challenge') || !has.has('challenge_win') || !has.has('challenge_streak_4')) {
      const { count: challengeSubmissions } = await supabase
        .from('challenge_submissions')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', profileId);
      const cs = challengeSubmissions ?? 0;

      if (cs >= 1 && !has.has('first_challenge')) {
        if (await awardBadge(profileId, 'first_challenge', { count: cs })) awarded.push('first_challenge');
      }

      // Challenge wins: check challenge_submissions with a winning rank
      const { count: challengeWins } = await supabase
        .from('challenge_submissions')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', profileId)
        .eq('rank', 1);
      if ((challengeWins ?? 0) >= 1 && !has.has('challenge_win')) {
        if (await awardBadge(profileId, 'challenge_win', { wins: challengeWins })) awarded.push('challenge_win');
      }

      // Challenge streak: 4 consecutive weeks — approximate with total submissions >= 4
      if (cs >= 4 && !has.has('challenge_streak_4')) {
        if (await awardBadge(profileId, 'challenge_streak_4', { count: cs })) awarded.push('challenge_streak_4');
      }
    }

    // ── Season badges ──
    if (!has.has('season_participant') || !has.has('season_top_10') || !has.has('season_champion')) {
      const { count: seasonCount } = await supabase
        .from('season_participants')
        .select('id', { count: 'exact', head: true })
        .eq('profile_id', profileId);

      if ((seasonCount ?? 0) >= 1 && !has.has('season_participant')) {
        if (await awardBadge(profileId, 'season_participant')) awarded.push('season_participant');
      }

      const { data: topFinishes } = await supabase
        .from('season_participants')
        .select('final_rank')
        .eq('profile_id', profileId)
        .not('final_rank', 'is', null)
        .lte('final_rank', 10);

      if ((topFinishes?.length ?? 0) >= 1 && !has.has('season_top_10')) {
        if (await awardBadge(profileId, 'season_top_10')) awarded.push('season_top_10');
      }

      const { data: championships } = await supabase
        .from('season_participants')
        .select('final_rank')
        .eq('profile_id', profileId)
        .eq('final_rank', 1);

      if ((championships?.length ?? 0) >= 1 && !has.has('season_champion')) {
        if (await awardBadge(profileId, 'season_champion')) awarded.push('season_champion');
      }
    }

    if (awarded.length > 0) {
      log.info('New badges awarded:', awarded);
    }

    // Fire push notifications for newly awarded badges (non-blocking)
    if (awarded.length > 0) {
      const NOTABLE_BADGES = new Set(['first_workout', 'first_pr', 'first_duel_win', 'bench_3plate', 'squat_4plate', 'deadlift_5plate', '1000lb_club', 'season_champion']);
      const notableBadge = awarded.find(k => NOTABLE_BADGES.has(k));
      if (notableBadge) {
        const def = BADGE_MAP.get(notableBadge);
        if (def) {
          void (async () => {
            try {
              await supabase.from('notifications').insert({
                user_id: profileId,
                type: 'badge_unlocked',
                title: `${def.emoji} Badge Unlocked: ${def.label}`,
                body: def.description,
                data: { type: 'badge_unlocked', badgeKey: notableBadge },
                is_read: false,
              });
            } catch { /* non-blocking */ }
          })();
        }
      }
    }
  } catch (err) {
    log.error('evaluateBadges failed:', err);
  }

  return awarded;
}
